/** Blocs d'une étape dans la vidéo : texte phrase par phrase (ou mot à mot), formule, question, erreur fréquente. */
import type { LessonBlock } from "@jange/shared";
import { Formula, md } from "@jange/lesson-player";
import { FPS, mdWords, sentences } from "@jange/shared";
import { handDrawnRadius, type Theme } from "../styles.js";

const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const IN = Math.round(0.45 * FPS);


/** Apparition : fondu + léger glissement ; en mode écriture, les mots arrivent un à un. */
export function Reveal({ at, frame, theme, children, text }: { at: number | undefined; frame: number; theme: Theme; children?: React.ReactNode; text?: string }) {
  if (at === undefined || frame < at) return null;
  const k = easeOut(clamp01((frame - at) / IN));
  if (theme.wordByWord && text !== undefined) {
    const words = mdWords(text);
    const perWord = 2.2; // images par mot : une phrase de 15 mots s'écrit en une seconde
    return (
      <span>
        {words.map((w, i) => {
          const kw = clamp01((frame - at - i * perWord) / 6);
          return <span key={i} style={{ opacity: kw, display: "inline-block", transform: `translateY(${(1 - kw) * 4}px)`, marginRight: "0.28em", fontWeight: w.bold ? 700 : undefined, fontStyle: w.italic ? "italic" : undefined, textDecoration: w.bold ? "underline" : undefined, textUnderlineOffset: 6 }}>{w.text}</span>;
        })}
      </span>
    );
  }
  // <span> en bloc, et non <div> : un texte peut être révélé à l'intérieur d'un <p>.
  return <span style={{ display: "block", opacity: k, transform: `translateY(${(1 - k) * 14}px)` }}>{children}</span>;
}

export function TextBlock({ block, reveals, frame, theme, size }: { block: Extract<LessonBlock, { type: "text" }> & { id: string }; reveals: Map<string, number>; frame: number; theme: Theme; size: number }) {
  const parts = sentences(block.md);
  const whole = reveals.get(block.id);
  return (
    <p style={{ margin: 0, fontSize: size, lineHeight: 1.42, color: theme.text }}>
      {parts.map((s, n) => {
        const at = whole ?? reveals.get(`${block.id}:${n}`);
        return (
          <span key={n} style={{ display: theme.wordByWord ? "inline" : "block", marginBottom: theme.wordByWord ? 0 : 10 }}>
            <Reveal at={at} frame={frame} theme={theme} text={s}><span dangerouslySetInnerHTML={{ __html: md(s) }} /></Reveal>
          </span>
        );
      })}
    </p>
  );
}

export function FormulaBlock({ block, at, frame, theme, boxed }: { block: Extract<LessonBlock, { type: "formula" }>; at: number | undefined; frame: number; theme: Theme; boxed?: boolean }) {
  return (
    <Reveal at={at} frame={frame} theme={theme}>
      <div className="jg-formula" style={{ color: theme.text, padding: theme.rough ? "10px 28px" : "16px 28px", background: theme.rough ? "transparent" : theme.card, borderRadius: theme.rough ? handDrawnRadius : 18, border: boxed ? `3px solid ${theme.rough ? theme.amber : theme.accent}` : theme.rough ? "none" : `1px solid ${theme.cardBorder}`, filter: theme.rough ? "url(#chalk)" : undefined, display: "inline-block", minWidth: "60%" }}>
        <Formula latex={block.latex} display={block.display} />
      </div>
    </Reveal>
  );
}

export function MistakeBlock({ block, at, frame, theme, size }: { block: Extract<LessonBlock, { type: "mistake" }>; at: number | undefined; frame: number; theme: Theme; size: number }) {
  return (
    <Reveal at={at} frame={frame} theme={theme}>
      <div style={{ background: theme.amberSoft, border: `${theme.rough ? 3 : 1}px solid ${theme.amber}`, borderRadius: theme.rough ? handDrawnRadius : 18, padding: "20px 26px", color: theme.text, fontSize: size, lineHeight: 1.42 }}>
        <div style={{ color: theme.amber, fontWeight: 800, fontSize: size * 0.72, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Attention · erreur fréquente</div>
        {theme.wordByWord ? <Reveal at={at} frame={frame} theme={theme} text={block.md} /> : <span dangerouslySetInnerHTML={{ __html: md(block.md) }} />}
      </div>
    </Reveal>
  );
}

const LETTERS = ["A", "B", "C", "D"];
export function QuizBlock({ block, at, revealAt, frame, theme, size, grid }: { block: Extract<LessonBlock, { type: "quiz" }> & { id: string }; at: number | undefined; revealAt: number | undefined; frame: number; theme: Theme; size: number; grid?: boolean }) {
  const revealed = revealAt !== undefined && frame >= revealAt;
  const kr = revealed ? easeOut(clamp01((frame - revealAt) / IN)) : 0;
  return (
    <Reveal at={at} frame={frame} theme={theme}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, color: theme.text }}>
        <div style={{ fontSize: size * 0.68, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.rough ? theme.amber : theme.accent }}>Question rapide</div>
        <div style={{ fontSize: size * 1.08, fontWeight: 700, lineHeight: 1.35 }} dangerouslySetInnerHTML={{ __html: md(block.question) }} />
        <div style={{ display: grid ? "grid" : "flex", gridTemplateColumns: grid ? "1fr 1fr" : undefined, flexDirection: "column", gap: 12 }}>
          {block.choices.map((c, i) => {
            const isAnswer = c.id === block.answerId;
            const ka = clamp01((frame - (at ?? 0) - 8 - i * 5) / 10);
            const bg = revealed && isAnswer ? (theme.rough ? theme.amberSoft : theme.accentSoft) : theme.rough ? "transparent" : theme.card;
            const border = revealed && isAnswer ? (theme.rough ? theme.amber : theme.accent) : theme.rough ? "transparent" : theme.cardBorder;
            return (
              <div key={c.id} style={{ opacity: ka * (revealed && !isAnswer ? 1 - 0.5 * kr : 1), transform: `translateX(${(1 - ka) * 16}px)`, display: "flex", alignItems: "center", gap: 18, padding: "14px 20px", borderRadius: theme.rough ? handDrawnRadius : 16, background: bg, border: `${theme.rough ? 3 : 2}px solid ${border}`, fontSize: size, fontWeight: 600 }}>
                <span style={{ width: size * 1.25, height: size * 1.25, borderRadius: 999, display: "grid", placeItems: "center", fontSize: size * 0.8, fontWeight: 800, background: revealed && isAnswer ? (theme.rough ? theme.amber : theme.accent) : theme.accentSoft, color: revealed && isAnswer ? "#0F1A33" : theme.text, flexShrink: 0 }}>{LETTERS[i]}</span>
                <span style={{ flex: 1 }}>{c.label}</span>
                {revealed && isAnswer && <span style={{ color: theme.rough ? theme.amber : theme.accent, fontSize: size * 1.1, opacity: kr }}>✓</span>}
              </div>
            );
          })}
        </div>
        {revealed && block.explanation && (
          <div style={{ opacity: kr, transform: `translateY(${(1 - kr) * 10}px)`, background: theme.rough ? "transparent" : theme.accentSoft, borderTop: theme.rough ? `2px solid ${theme.cardBorder}` : "none", borderRadius: 16, padding: theme.rough ? "14px 0 0" : "16px 20px", fontSize: size * 0.9, lineHeight: 1.42 }}>
            <b>Bien vu !</b> {block.explanation}
          </div>
        )}
      </div>
    </Reveal>
  );
}
